import * as THREE from 'three';
import { basic, emis, smoke, std } from '../materials.ts';
import type { Rng } from '../rand.ts';
import { M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Cuervo de hollín (docs/CRIATURAS.md): a big ragged crow made of soot, the Era I flying swarm. One model is
 * one bird and the game draws a flock of them instanced, so it is cheap (flat feather blades, few materials)
 * and reads at a glance as a black bird with two ember eyes and a strong wing silhouette. Slightly wrong: a
 * wing hand of too many fingers, torn feathers, a bone-pale beak, soot streaming off the wingtips and tail.
 *
 * It flies: the hover height is its rest pose (the clips beat the wings and bob it). At rest the wings are
 * spread flat, mid-beat. The right wing is +X; on it +Z on `arm` lifts the wing and +Y on `hand` sweeps the
 * hand back (the left one mirrors). +X on `body` pitches the nose down; +X on `jaw` opens the beak.
 */

/** Height of the model's origin over the ground while it flies (the clips drop it from here to die). */
export const CROW_HOVER = 0.72;

/** Grows a flat geometry of feather blades and remembers which vertices are ragged tips (they boil). */
export class Plumage {
  readonly pos: number[] = [];
  readonly idx: number[] = [];
  readonly tips: number[] = [];
  constructor(private readonly k: number, private readonly rng: Rng) {}
  private v(p: THREE.Vector3, tip = false): number {
    const i = this.pos.length / 3;
    this.pos.push(p.x * this.k, p.y, p.z);
    if (tip) this.tips.push(i);
    return i;
  }
  private tri(a: number, b: number, c: number): void {
    this.idx.push(a, b, c);
  }
  /**
   * A feather from `base` along `dir` (its plane is ~horizontal): broad at the root, tapered or torn at the tip.
   * `slot` > 0 notches the vane near the tip (an emarginated "finger" of the wing hand).
   */
  feather(base: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, torn = false, slot = 0, droopK = 0.05, face = V(0, 1, 0)): void {
    const d = dir.clone().normalize();
    const n = d.clone().cross(face).normalize();
    const f = n.clone().cross(d).normalize();
    const droop = (k: number): THREE.Vector3 => f.clone().multiplyScalar(-droopK * len * k * k);
    const at = (k: number, s: number): THREE.Vector3 => base.clone().addScaledVector(d, len * k).addScaledVector(n, s).add(droop(k));
    const j = (): number => (this.rng() - 0.5) * w * 0.4;
    const b0 = this.v(at(0, -w * 0.35)), b1 = this.v(at(0, w * 0.35));
    const mk = 0.55 - slot * 0.12;
    const m0 = this.v(at(mk, -w * 0.5 + j())), m1 = this.v(at(mk, w * 0.5 + j()));
    if (slot < 0) {
      // A small covert: three faces, a pointed tip.
      const t = this.v(at(1, j()), true);
      this.tri(b0, b1, m1);
      this.tri(b0, m1, m0);
      this.tri(m0, m1, t);
      return;
    }
    // A thin raised shaft keeps the blade from reading as paper.
    const c = this.v(at(mk * 0.9, 0).addScaledVector(f, 0.008));
    this.tri(b0, b1, c);
    this.tri(b1, m1, c);
    this.tri(c, m0, b0);
    if (torn) {
      const t0 = this.v(at(0.78 + this.rng() * 0.1, -w * 0.3), true), t1 = this.v(at(1, w * 0.12), true);
      const g = this.v(at(0.8, w * 0.02));
      this.tri(m0, c, g);
      this.tri(c, m1, g);
      this.tri(m0, g, t0);
      this.tri(g, m1, t1);
    } else {
      const t = this.v(at(1, 0), true);
      const s0 = this.v(at(0.82, -w * (0.3 - slot * 0.12)), true), s1 = this.v(at(0.82, w * (0.3 - slot * 0.1)), true);
      this.tri(m0, c, s0);
      this.tri(c, m1, s1);
      this.tri(c, s1, t);
      this.tri(c, t, s0);
    }
  }
  /** A flat plate (coverts): a fan over the given outline points. */
  plate(pts: THREE.Vector3[]): void {
    const ids = pts.map((p) => this.v(p));
    for (let i = 1; i < ids.length - 1; i++) this.tri(ids[0]!, ids[i]!, ids[i + 1]!);
  }
  /**
   * The blades as geometry: `geo` the oily black of the vanes, `edge` their ashen tips (drybrushed, as on a
   * painted miniature), both on the same vertices; `all` in one piece (for the soot fringe).
   */
  geometry(): { geo: THREE.BufferGeometry; edge: THREE.BufferGeometry; all: THREE.BufferGeometry; rest: Float32Array; tips: number[] } {
    const arr = new Float32Array(this.pos);
    const attr = new THREE.BufferAttribute(arr, 3);
    // Mirrored blades wind the other way: flip them so both wings face up alike.
    const idx = this.k < 0 ? this.idx.map((_, i, a) => a[i - (i % 3) + (2 - (i % 3))]!) : this.idx;
    const tip = new Set(this.tips);
    const dark: number[] = [], ash: number[] = [];
    for (let i = 0; i < idx.length; i += 3) {
      const t = idx.slice(i, i + 3);
      (t.some((v) => tip.has(v)) ? ash : dark).push(...t);
    }
    const make = (ix: number[]): THREE.BufferGeometry => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', attr);
      geo.setIndex(ix);
      geo.computeVertexNormals();
      return geo;
    };
    return { geo: make(dark), edge: make(ash), all: make(idx), rest: arr.slice(), tips: this.tips };
  }
}

export const crow = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 47);
  const { g, rng, R } = c;
  g.position.y = CROW_HOVER;

  // Soot with an oily sheen (the blue-violet of a crow's back under the moon); a cold bone beak; ink.
  const plume = std('#100e16', { roughness: 0.5, metalness: 0.25, side: THREE.DoubleSide, bumpScale: 0.02 });
  const beakM = std('#b9b5ad', { roughness: 0.55, bumpScale: 0.02 });
  const ink = std('#05040a', { roughness: 0.05, metalness: 0.6, bumpScale: 0 });
  const haze = smoke('#2c2734', 0.5, { side: THREE.DoubleSide });
  const wispM = basic('#25212b', 0.42);

  const ashM = std('#2e2b35', { roughness: 0.85, side: THREE.DoubleSide, bumpScale: 0.03 });
  // The wing hand and the tail were burnt at the edges: their tips still smoulder, faintly.
  const burnt = emis('#2e2a31', '#a32c0a', 0.06, { roughness: 0.85, side: THREE.DoubleSide, bumpScale: 0.03 });
  const boil: { geo: THREE.BufferGeometry; rest: Float32Array; tips: number[]; amp: number }[] = [];
  const ribs: Ribbon[] = [];
  /** Hangs a set of blades on a bone: black vanes, ashen tips that boil. */
  const wear = (pl: Plumage, parent: THREE.Object3D, amp: number, edge = ashM): ReturnType<Plumage['geometry']> => {
    const gm = pl.geometry();
    M(gm.geo, plume, parent);
    M(gm.edge, edge, parent);
    boil.push({ geo: gm.geo, rest: gm.rest, tips: gm.tips, amp });
    return gm;
  };

  /* ---------- body, head, beak ---------- */
  const body = joint(g, V(0, 0.32, 0), 'body');
  const head = joint(body.abs, V(0, 0.4, 0.24), 'head');
  const jaw = joint(head.abs, V(0, 0.415, 0.4), 'jaw');
  const legs = joint(body.abs, V(0, 0.24, -0.02), 'legs');
  const tail = joint(body.abs, V(0, 0.33, -0.26), 'tail');

  // A teardrop trunk: deep in the chest, tapering to the tail.
  const prof = [V(0.0, -0.34, 0), V(0.06, -0.28, 0), V(0.115, -0.15, 0), V(0.15, 0.0, 0), V(0.15, 0.12, 0), V(0.12, 0.22, 0), V(0.06, 0.29, 0), V(0.0, 0.31, 0)];
  const trunk = M(new THREE.LatheGeometry(prof.map((p) => new THREE.Vector2(p.x, p.y)), 8), std('#0c0a10', { roughness: 0.8, bumpScale: 0.03 }), body.abs);
  trunk.rotation.x = Math.PI / 2 - 0.08;
  trunk.scale.set(1, 1, 0.88);
  trunk.position.set(0, 0.32, 0);
  // Shaggy breast, mantle and flanks: ragged blades standing off the trunk in layers.
  const ruff = new Plumage(1, rng);
  for (let i = 0; i < 5; i++) {
    const a = (i / 4 - 0.5) * 2.4;
    ruff.feather(V(Math.sin(a) * 0.11, 0.24 + Math.abs(a) * 0.02, 0.2 + Math.cos(a) * 0.02), V(Math.sin(a) * 0.45, -0.6, -0.55), 0.17, 0.07, i % 2 === 0, 0, -0.1, V(Math.sin(a), -0.5, 0.8));
  }
  for (let row = 0; row < 3; row++) {
    for (let i = 0; i < 4 - (row === 2 ? 1 : 0); i++) {
      const n = row === 2 ? 3 : 4;
      const x = (i / (n - 1) - 0.5) * (0.2 - row * 0.03);
      ruff.feather(V(x, 0.46 - row * 0.012, 0.16 - row * 0.12), V(x * 1.4, 0.08 - row * 0.04, -1), 0.17 + row * 0.03 + R() * 0.03, 0.065, false, -1, 0.15);
    }
  }
  for (const k of [-1, 1]) {
    for (let i = 0; i < 4; i++) {
      ruff.feather(V(k * 0.14, 0.36, 0.16 - i * 0.1), V(k * 0.25, -0.3, -1), 0.18, 0.09, i === 2, -1, -0.15, V(k, 0.5, 0));
      if (i < 3) ruff.feather(V(k * 0.12, 0.26, 0.13 - i * 0.11), V(k * 0.2, -0.45, -1), 0.17, 0.09, false, -1, -0.15, V(k, -0.3, 0));
    }
  }
  // The body's tips are only a shade lighter: the drybrush catches the wings, not the belly.
  wear(ruff, body.abs, 0.01, std('#1f1c25', { roughness: 0.8, side: THREE.DoubleSide, bumpScale: 0.03 }));

  const skull = M(new THREE.SphereGeometry(0.095, 8, 6), plume, head.abs);
  skull.scale.set(0.84, 0.86, 1.3);
  skull.position.set(0, 0.432, 0.33);
  skull.rotation.x = 0.12;
  // The heavy raven beak, bone-pale and hooked; the lower half on the jaw.
  const beak = M(new THREE.ConeGeometry(0.042, 0.23, 5), beakM, head.abs);
  beak.rotation.x = Math.PI / 2 + 0.14;
  beak.scale.set(0.72, 1, 1.15);
  beak.position.set(0, 0.437, 0.51);
  const lower = M(new THREE.ConeGeometry(0.03, 0.17, 4), beakM, jaw.abs);
  lower.rotation.x = Math.PI / 2 - 0.08;
  lower.scale.set(0.8, 1, 0.7);
  lower.position.set(0, 0.408, 0.49);
  // A thread of ink hanging off the beak.
  const drip = M(new THREE.SphereGeometry(0.008, 4, 4), ink, jaw.abs);
  drip.scale.set(1, 5.5, 1);
  drip.position.set(0.004, 0.37, 0.53);
  // Ember eyes under a hard brow, and a crest of torn hackles raking back.
  const eyeM = emis('#ffd2a0', '#ff5a1e', 2.2);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.017, 5, 4), eyeM, head.abs).position.set(k * 0.056, 0.452, 0.388);
    glowSprite(head.abs, '#ff4a14', 0.12, V(k * 0.07, 0.462, 0.4), 0.95);
  }
  for (const k of [-1, 1]) {
    const brow = M(new THREE.BoxGeometry(0.06, 0.016, 0.05), plume, head.abs);
    brow.position.set(k * 0.05, 0.476, 0.385);
    brow.rotation.set(0.25, k * 0.35, -k * 0.45);
  }
  const hackles = new Plumage(1, rng);
  for (let i = 0; i < 4; i++) {
    const x = (i - 1.5) * 0.03;
    hackles.feather(V(x, 0.49, 0.32 - Math.abs(x)), V(x * 2, 0.45 + R() * 0.2, -1), 0.14 + rng() * 0.05, 0.035, i % 2 === 1);
  }
  for (let i = 0; i < 3; i++) {
    const x = (i - 1) * 0.04;
    hackles.feather(V(x, 0.38, 0.36), V(x * 2, -1, -0.7), 0.11, 0.035, true);
  }
  wear(hackles, head.abs, 0.012);

  /* ---------- wings: arm (coverts, secondaries) and hand (too many primaries) ---------- */
  const skel: Record<string, THREE.Object3D> = { body: body.p, head: head.p, jaw: jaw.p };
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const arm = joint(body.abs, V(k * 0.1, 0.385, 0.1), `arm${s}`);
    const hand = joint(arm.abs, V(k * 0.5, 0.39, 0.11), `hand${s}`);
    skel[`arm${s}`] = arm.p;
    skel[`hand${s}`] = hand.p;
    const inner = new Plumage(k, rng);
    inner.plate([V(0.08, 0.388, 0.13), V(0.3, 0.398, 0.17), V(0.5, 0.394, 0.12), V(0.5, 0.384, -0.0), V(0.3, 0.386, -0.03), V(0.08, 0.38, -0.06)]);
    // Coverts over the leading edge, shingled back: the wing's texture at a glance.
    for (let i = 0; i < 5; i++) {
      const u = i / 4;
      inner.feather(V(0.1 + u * 0.4, 0.4, 0.16 - Math.abs(u - 0.5) * 0.06), V(0.15 + u * 0.1, 0.02, -1), 0.17 + R() * 0.04, 0.1, false, -1, 0.1);
    }
    // Secondaries: long and ragged, with gaps where some have burnt away.
    for (let i = 0; i < 7; i++) {
      const u = i / 6;
      if (i === 4) continue;
      const x = 0.12 + u * 0.37;
      inner.feather(V(x, 0.382, 0.07), V(0.12 * u - 0.05, 0, -1), 0.33 - u * 0.07 + R() * 0.05, 0.085, i % 3 === 1);
    }
    wear(inner, arm.abs, 0.012);
    const outer = new Plumage(k, rng);
    outer.plate([V(0.49, 0.392, 0.13), V(0.64, 0.392, 0.12), V(0.71, 0.388, 0.03), V(0.62, 0.384, -0.07), V(0.49, 0.383, 0.0)]);
    // Primaries: eight splayed fingers where a crow has five, the outer ones notched, two torn short.
    for (let i = 0; i < 8; i++) {
      const u = i / 7;
      const a = 1.2 - u * 1.32;
      const len = 0.34 + u * 0.27 - (i === 7 ? 0.07 : 0) + R() * 0.04;
      outer.feather(V(0.5 + u * 0.1, 0.388, 0.08 + u * 0.04), V(Math.cos(a), 0, -Math.sin(a)), len, 0.08 - u * 0.012, i === 2 || i === 5, u > 0.4 ? 1 : 0);
    }
    const og = wear(outer, hand.abs, 0.016, burnt);
    // A fringe of soot behind the hand, a little longer, boiling on its own.
    const fringe = og.all.clone();
    const fm = M(fringe, haze, hand.abs);
    fm.position.set(k * 0.06, -0.015, -0.07);
    boil.push({ geo: fringe, rest: og.rest.slice(), tips: og.tips, amp: 0.045 });
    // Soot streaming off the wingtip.
    ribs.push(new Ribbon(hand.abs, [V(k * 0.98, 0.38, -0.12), V(k * 1.03, 0.36, -0.34), V(k * 0.98, 0.38, -0.56), V(k * 1.04, 0.34, -0.8)], 0.02, wispM, rng, 0.05));
  }

  /* ---------- tail: a wedge of torn feathers ---------- */
  skel.tail = tail.p;
  const tl = new Plumage(1, rng);
  tl.plate([V(-0.07, 0.34, -0.2), V(0.07, 0.34, -0.2), V(0.06, 0.33, -0.36), V(-0.06, 0.33, -0.36)]);
  for (let i = 0; i < 7; i++) {
    const u = i / 6 - 0.5;
    tl.feather(V(u * 0.12, 0.335 - Math.abs(u) * 0.01, -0.22), V(u * 0.9, -0.05, -1), 0.4 - Math.abs(u) * 0.16 + R() * 0.03, 0.075, i === 1 || i === 4);
  }
  const tg = wear(tl, tail.abs, 0.014, burnt);
  const tf = tg.all.clone();
  M(tf, haze, tail.abs).position.set(0, -0.01, -0.05);
  boil.push({ geo: tf, rest: tg.rest.slice(), tips: tg.tips, amp: 0.05 });
  ribs.push(new Ribbon(tail.abs, [V(0, 0.32, -0.55), V(0.04, 0.3, -0.75), V(-0.03, 0.31, -0.95), V(0.02, 0.27, -1.18)], 0.024, wispM, rng, 0.06));

  /* ---------- talons, drawn up under the belly ---------- */
  skel.legs = legs.p;
  for (const k of [-1, 1]) {
    const hip = V(k * 0.05, 0.24, -0.02), ankle = V(k * 0.06, 0.15, -0.1);
    limb(legs.abs, hip, ankle, 0.017, 0.011, plume, 4);
    for (let i = 0; i < 3; i++) {
      const cl = M(new THREE.ConeGeometry(0.009, 0.07, 3), beakM, legs.abs);
      cl.position.set(ankle.x + (i - 1) * 0.016 * k, ankle.y - 0.015, ankle.z + 0.025);
      cl.rotation.set(Math.PI / 2 + 0.7, 0, (i - 1) * 0.3);
    }
  }

  // Embers shed from the soot, drifting off the back and dying.
  const embers: { s: THREE.Sprite; x: number; z: number; ph: number }[] = [];
  for (let i = 0; i < 3; i++) embers.push({ s: glowSprite(body.abs, '#ff6a24', 0.07, V(0, 0, 0), 0.8, true), x: R() * 0.25, z: -0.1 - rng() * 0.2, ph: i / 3 });

  const t = (n: number): void => {
    for (const r of ribs) r.step();
    for (const b of boil) {
      const p = b.geo.attributes.position as THREE.BufferAttribute;
      const a = p.array as Float32Array;
      a.set(b.rest);
      for (const i of b.tips) {
        a[i * 3] = b.rest[i * 3]! + R() * b.amp;
        a[i * 3 + 1] = b.rest[i * 3 + 1]! + R() * b.amp;
        a[i * 3 + 2] = b.rest[i * 3 + 2]! + R() * b.amp * 1.5;
      }
      p.needsUpdate = true;
    }
    for (const e of embers) {
      const p = (e.ph + n * 0.27) % 1;
      e.s.position.set(e.x + Math.sin(p * 5 + e.ph * 9) * 0.04, 0.36 + p * 0.12, e.z - p * 0.45);
      e.s.material.opacity = 0.85 * (1 - p) * (p < 0.08 ? p / 0.08 : 1);
    }
  };
  t(0);
  return done(c, CROW_HOVER + 0.58, t, undefined, bonesOf(c, skel));
};
